/**
 * Layout and typography extracted from Figma node 16692:5503 (Step 2 — About your company).
 * Update only from Figma MCP export, not by guessing.
 */
import { ATLAS_LENS_COLORS } from '../Form/formFieldSpec';
export const ONBOARDING_STEP2_FIGMA = {
  dialog: {
    width: 800,
    height: 640,
    borderRadius: 12,
    /** Atlas component.modal.boxShadow → core.shadow.high */
    shadow: 'var(--lens-component-modal-box-shadow)',
    /** Figma Dialog node 16692:5483 — pb spacing below footer */
    paddingBottom: 16,
  },
  content: {
    paddingBottom: 24,
  },
  formSection: {
    paddingX: 48,
    paddingY: 40,
    sectionGap: 24,
  },
  formField: {
    labelGap: 4,
    labelToInputGap: 8,
    inputHeight: 40,
    inputPaddingX: 12,
    inputPaddingY: 8,
    inputRadius: 8,
    totalHeight: 64,
    /** Reserve space for dropdown chevron (Figma 16692:5588). */
    selectIconGutter: 32,
  },
  dropdownRow: {
    gap: 24,
  },
  regions: {
    labelHeight: 16,
    columnGap: 24,
    checkboxGap: 12,
    rowPaddingY: 12,
    checkboxSize: 24,
    checkboxRadius: 4,
  },
  footer: {
    /** Dot stepper footer — 16692:5494 (all wizard steps). */
    height: 56,
    paddingX: 48,
    paddingTop: 16,
    paddingBottom: 0,
    dotStepperTop: 30,
    contentMinHeight: 40,
    buttonHeight: 40,
    buttonMinWidth: 105,
    /** Step 6 primary — "Finish setup" (Figma 16692:5652). */
    finishButtonMinWidth: 140,
    stepperGap: 8,
    dotSize: 12,
    actionGap: 12,
    skipButtonRadius: 12,
    skipButtonPaddingX: 12,
    skipButtonPaddingY: 8,
    menuMinWidth: 171,
    menuBorderRadius: 8,
    menuShadow: 'var(--lens-component-dropdown-container-box-shadow)',
  },
  buttonTypography: {
    fontSize: 16,
    lineHeight: '25.5px',
    fontWeight: 600,
    letterSpacing: '1px',
  },
  skipButtonTypography: {
    fontSize: 16,
    lineHeight: '25.5px',
    fontWeight: 600,
    letterSpacing: '0.16px',
  },
  menuItemTypography: {
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 400,
    letterSpacing: '0.2px',
  },
  typography: {
    /** Semantic/Font/Title/H2 Display - Emphasis */
    h2Display: {
      fontSize: 26,
      lineHeight: 34,
      fontWeight: 600,
      letterSpacing: 0,
    },
    /** Semantic/Font/Text/Md */
    textMd: {
      fontSize: 14,
      lineHeight: 20,
      fontWeight: 400,
      letterSpacing: 0.2,
    },
    /** Semantic/Font/Text/Sm - Emphasis (field labels) */
    labelEmphasis: {
      fontSize: 12,
      lineHeight: 16,
      fontWeight: 600,
      letterSpacing: 0.3,
    },
    /** Semantic/Font/Text/Sm (required asterisk) */
    labelRequired: {
      fontSize: 12,
      lineHeight: 16,
      fontWeight: 400,
      letterSpacing: 0.3,
    },
    /** Semantic/Font/Text/Body (select values) */
    body: {
      fontSize: 16,
      lineHeight: 24,
      fontWeight: 400,
      letterSpacing: 0.2,
    },
    /** Semantic/Font/Text/Body - Emphasis (radio-card titles, node I16939:3718) */
    bodyEmphasis: {
      fontSize: 16,
      lineHeight: 24,
      fontWeight: 600,
      letterSpacing: 0.2,
    },
    /** Semantic/Font/Number/Sm — character counters (Figma Schedule step). */
    numberSm: {
      fontSize: 12,
      lineHeight: 16,
      fontWeight: 400,
      letterSpacing: 0.3,
    },
  },
  colors: ATLAS_LENS_COLORS,
} as const;

/**
 * Step 2 Your training package — Figma 16939:3708 (default) / 16940:6280 (selected).
 */
export const ONBOARDING_STEP_TRAINING_PACKAGE_FIGMA = {
  formSection: {
    paddingX: 48,
    paddingY: 40,
    sectionGap: 24,
    /** Page header content — gap between title and subtitle (Figma 16939:3708). */
    headerSubtitleGap: 8,
  },
  optionCard: {
    listGap: 16,
    paddingX: 12,
    paddingY: 12,
    minHeight: 68,
    borderRadius: 8,
    radioGap: 16,
    labelGap: 4,
    radioSize: 24,
    radioIndicatorSize: 8,
    radioBorderWidth: 1,
  },
  colors: ATLAS_LENS_COLORS,
} as const;

/** Step 5 Schedule and audience — Figma onboarding form (priority months / months to avoid / context). */
export const ONBOARDING_STEP_SCHEDULE_AUDIENCE_FIGMA = {
  formSection: {
    paddingX: 48,
    paddingY: 40,
    sectionGap: 24,
    headerSubtitleGap: 8,
  },
  dropdownRow: {
    gap: 24,
    fieldMinWidth: 340,
  },
  formField: {
    labelGap: 4,
    labelToInputGap: 8,
    labelRowHeight: 16,
    inputHeight: 40,
    inputPaddingX: 12,
    inputPaddingY: 8,
    inputRadius: 8,
  },
  /** Figma multiselect — (6) January, February, March, April... */
  multiSelect: {
    maxVisibleLabels: 4,
    /** Reserve space for dropdown chevron (Figma 16692:5588). */
    selectIconGutter: 32,
  },
  textArea: {
    labelRowHeight: 16,
    minHeight: 84,
    contentMinHeight: 48,
    paddingX: 12,
    paddingY: 8,
    borderRadius: 8,
  },
  colors: ATLAS_LENS_COLORS,
} as const;

/** Step 5 Your brand — Figma node 16692:5621 and variants 16692:5661 / 16692:5980 / 16692:6030. */
export const ONBOARDING_STEP5_FIGMA = {
  formSection: {
    paddingX: 48,
    paddingY: 32,
    sectionGap: 24,
  },
  formField: {
    labelToInputGap: 8,
    totalHeight: 64,
  },
  inlineAttach: {
    width: 153,
    height: 40,
    borderRadius: 12,
    /** Gap between attach button and format hints — Figma Spacing/3. */
    gap: 24,
    /** Gap between format hint segments — Figma Details row. */
    detailsGap: 12,
    separatorSize: 4,
    maxWidth: 458,
    maxWidthGuidelines: 481,
  },
  filesArray: {
    gapToAttach: 12,
    rowHeight: 88,
    thumbnailSize: 64,
    contentPadding: 12,
  },
  radio: {
    height: 32,
    size: 24,
    borderWidth: 1.4,
    indicatorSize: 8,
    optionPaddingY: 4,
    /** Horizontal gap between Yes and No — Figma radio button array. */
    gap: 24,
    /** Gap between radio circle and label — Figma radio button row. */
    labelGap: 12,
  },
  colorPanel: {
    /** Frame 1 — bordered brand colors container (Figma 16692:5691). */
    frameMaxWidth: 704,
    paddingX: 24,
    paddingTop: 24,
    paddingBottom: 64,
    borderRadius: 12,
    columnWidth: 316,
    columnGap: 24,
    sectionHeaderHeight: 44,
    /** Figma form field — label (16) + gap (8) + input (40). */
    formFieldHeight: 64,
    /** Gap between "Brand colors" title and subtitle — Figma section header heading. */
    sectionTitleGap: 8,
    formFieldsGap: 24,
    /** Figma Spacing/3 — gap between color swatch and hex input text. */
    swatchGap: 12,
    swatchSize: 20,
  },
} as const;

/** Step 6 complete — Figma loading / success confirmation (draft training plan ready). */
export const ONBOARDING_STEP6_FIGMA = {
  loading: {
    spinnerSize: 40,
    spinnerGap: 24,
    contentGap: 8,
    messageGap: 16,
    blockMaxWidth: 360,
    /** Semantic/Font/Title/H3 Lg - Emphasis */
    title: {
      fontSize: 22,
      lineHeight: 28,
      fontWeight: 600,
    },
  },
  complete: {
    /** Figma empty state — node empty state / Image + Content (712×294). */
    emptyStateWidth: 712,
    emptyStateHeight: 294,
    emptyStatePaddingY: 12,
    illustrationWidth: 160,
    illustrationHeight: 132,
    contentBlockHeight: 90,
    contentGap: 8,
    titleHeight: 34,
    copyHeight: 48,
    illustrationGap: 48,
  /** Dialog content viewport — centers empty state vertically (Figma 16692:5483). */
    contentViewportHeight: 400,
    primaryButtonMinWidth: 214,
  },
} as const;

/** Step 1 Welcome — Figma node 16692:5463 (dialog 16692:5483). */
export const ONBOARDING_STEP1_FIGMA = {
  content: {
    height: 567,
  },
  leftColumn: {
    width: 400,
    paddingX: 32,
    sectionGap: 48,
    contentWidth: 336,
  },
  header: {
    titleSubtitleGap: 8,
  },
  formFields: {
    gap: 24,
  },
  /** Dot stepper footer — Figma 16692:5494 (shared across all steps). */
  footer: {
    height: 56,
    paddingTop: 16,
    paddingX: 48,
    paddingBottom: 0,
    dotStepperTop: 30,
  },
  illustration: {
    width: 646,
    height: 400,
    screenInsetTop: '13.4%',
    screenInsetRight: '13.86%',
    screenInsetBottom: '13.4%',
    screenInsetLeft: '13.86%',
  },
} as const;

/**
 * Figma node 17786:17464 — layout metrics only (Atlas Lens colors/typography in banner component).
 * Decoration layer: 17786:17472.
 */
export const ONBOARDING_COURSES_BANNER_FIGMA = {
  actionsWrap: {
    width: 386,
    height: 72,
    actionsRowWidth: 209,
    actionsRowHeight: 36,
    actionsPaddingTop: 16,
    actionsPaddingLeft: 40,
    actionsGap: 16,
  },
  button: {
    minWidth: 153,
  },
  /** Rectangle 3 → 4 in DOM. `left` from Figma; height fills banner (bottom-anchored). */
  decoration: {
    rectPrimary: {
      layoutWidth: 303,
      assetWidth: 303,
      assetHeight: 104,
      centerOffsetX: -41.5,
      zIndex: 1,
    },
    rectSecondary: {
      layoutWidth: 298,
      assetWidth: 282,
      assetHeight: 104,
      centerOffsetX: 76,
      zIndex: 2,
    },
  },
} as const;

/**
 * Figma node 17247:90642 — Legacy sample preview Announcement on /courses (CEE-1023).
 */
export const LEGACY_SAMPLE_BANNER_FIGMA = {
  actionsWrap: {
    width: 386,
    height: 72,
    actionsRowWidth: 209,
    actionsRowHeight: 36,
    actionsPaddingTop: 4,
    actionsPaddingLeft: 40,
    actionsGap: 16,
  },
  button: {
    minWidth: 130,
  },
  decoration: {
    rectPrimary: {
      layoutWidth: 303,
      assetWidth: 303,
      assetHeight: 280,
      centerOffsetX: -41.5,
      centerOffsetY: -10,
      zIndex: 1,
    },
    rectSecondary: {
      layoutWidth: 298,
      assetWidth: 298,
      assetHeight: 280,
      centerOffsetX: 76,
      centerOffsetY: -10,
      zIndex: 2,
    },
  },
} as const;
